import React, { useCallback, useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import { CatmullRomCurve3, Mesh, Quaternion, Vector3 } from 'three';

import { Segment3D } from '@paragon/three-shared';

import { getPointFromPolynomial } from '../../library/getPolynomialPoint';
import { build, lerpGeometries } from '../util/LerpGeometry';

interface DeflectionSplineVisualProps {
  readonly linkSegment: Segment3D;
  readonly coefficients: readonly number[];
  readonly scale: number;
}

export function DeflectionSplineVisual(props: DeflectionSplineVisualProps) {
  const timerHandleRef = useRef<number | undefined>(undefined);
  const timeStepRef = useRef<number>(0);
  const meshRef = useRef<Mesh | null>(null);

  const { invalidate } = useThree();

  const { linkSegment } = props;
  const start = new Vector3(linkSegment.start.x, linkSegment.start.y, linkSegment.start.z);
  const end = new Vector3(linkSegment.end.x, linkSegment.end.y, linkSegment.end.z);
  const normalizedLinkDirection = new Vector3().copy(end).sub(start).normalize();
  const quaternion = new Quaternion().setFromUnitVectors(new Vector3(1, 0, 0), normalizedLinkDirection);
  const position = new Vector3().copy(start).setZ(start.z + 2);

  const length = new Vector3().copy(start).sub(end).length();
  const valueCount = 26;
  const valueSpacing = length / (valueCount - 1);

  const startPoints = [];
  const endPoints = [];
  for (let index = 0; index < valueCount; index += 1) {
    startPoints.push(new Vector3(index * valueSpacing, 0, 0));
    endPoints.push(
      new Vector3(
        index * valueSpacing,
        getPointFromPolynomial(index * valueSpacing, props.coefficients) * props.scale,
        0,
      ),
    );
  }
  const startCurve = new CatmullRomCurve3(startPoints);
  const endCurve = new CatmullRomCurve3(endPoints);

  // These are never added to the scene, so we don't need to dispose of them
  const startGeometry = build(startCurve, 15, 2);
  const endGeometry = build(endCurve, 15, 2);

  const lerpGeometry = useCallback(() => {
    if (!meshRef.current || !meshRef.current.geometry) return;
    lerpGeometries(startGeometry, endGeometry, meshRef.current.geometry, timeStepRef.current);
    invalidate();
  }, [endGeometry, invalidate, startGeometry]);

  const tick = useCallback(() => {
    if (timeStepRef.current >= 1) {
      clearInterval(timerHandleRef.current);
      return;
    }
    timeStepRef.current = Math.min(timeStepRef.current + 0.1, 1);
    lerpGeometry();
  }, [lerpGeometry]);

  useEffect(() => {
    timerHandleRef.current = setInterval(tick, 300);
    return () => clearInterval(timerHandleRef.current);
  }, [tick]);

  return (
    <object3D position={position.toArray()} quaternion={quaternion.toArray()}>
      <mesh ref={meshRef} geometry={startGeometry.clone()}>
        <meshBasicMaterial color={0xfff000} />
      </mesh>
    </object3D>
  );
}
